import { CHILIPIPER_WEB_DATA_ENV_GLOBAL } from './constants'

/**
 * Environment access for the web-data bundles.
 *
 * Every Chili Piper app reads its runtime config from `window._env_`, assigned by the env-config.js
 * its deployment serves. The web-data bundles run inside other people's pages, and some of those
 * pages are our own apps. Had their env-config assigned `_env_` too, loading a bundle would have
 * replaced the host's values with ours: module federation resolves remote app hosts from `_env_` on
 * every route change, so the host would have started resolving remotes against the web-data
 * deployment's paths.
 *
 * The web-data deployment therefore publishes its config under CHILIPIPER_WEB_DATA_ENV_GLOBAL (see
 * ENV_CONFIG_GLOBAL_NAME in generate-env-config.sh), and the bundles read that and nothing else on
 * the page. The global is left in place once set: both bundles import the same env-config URL, and
 * a dynamic import runs a module once per page, so the second bundle finds the values the first
 * one's import left behind rather than an import that resolves without assigning anything.
 */

/**
 * Imports an `env-config.js` for its side effect of assigning the web-data global. Returns false
 * when the import failed, after handing the error to `onError`.
 */
export const loadEnvConfig = async (
  modulePath: string,
  onError: (error: unknown) => void
): Promise<boolean> => {
  try {
    await import(/* @vite-ignore */ modulePath)
    return true
  } catch (error) {
    onError(error)
    return false
  }
}

// Local development and tests load no config file; there the value comes from the process
// environment. In a browser `process` does not exist and the fallback is the empty string, never
// the host page's own `_env_`.
const ambient = (key: string): string =>
  typeof process === 'undefined' ? '' : (process.env[key] ?? '')

/** Reads a value from the web-data deployment's runtime config. */
export const getEnv = (key: string): string =>
  window[CHILIPIPER_WEB_DATA_ENV_GLOBAL]?.[key] ?? ambient(key)
